import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { TlkModalWindowService } from '@itero/ui-toolkit-angular/modal-window';
import { DialogRef } from '@angular/cdk/dialog';
import { Patient } from '../../state-machine/patient-forms.types';
import { checkCardEditable } from '../../state-machine/patient-forms.guards';
import { PatientFormsServicesToken } from '../../services/patient-forms-services.token';
import { PatientFormsMachineService, PatientFormsMachineState } from '../../services/patient-forms-machine.service';
import { PatientSearchContentComponent } from '../patient-search/patient-search.component';
import { PatientLinkingContentComponent, PatientLinkingResult, PatientLinkingData } from '../patient-linking/patient-linking.component';
import { PatientEditorContentComponent, PatientEditorResult, PatientEditorMode } from '../patient-editor/patient-editor.component';
import { BI_CONTAINER, BI_DIMENSIONS, BI_FEATURE, type BiDimensionValues } from '../../observability/bi-constants';
import { buildSaveDimensions } from '../../observability/build-save-dimensions';
import { UnifiedPatientsBiService } from '../../observability/bi.service';
import translations from '../../assets/i18n/en.json';

@Component({
	selector: 'rx-unified-patient',
	templateUrl: './unified-patient.component.html',
	styleUrls: ['./unified-patient.component.scss'],
	host: { '[class.is-loading]': 'initializing' }
})
export class UnifiedPatientComponent implements OnInit, OnDestroy {
	private formUpdate$ = new Subject<void>();
	private subscription!: Subscription;
	private editorRef: DialogRef<PatientEditorResult> | null = null;
	private lastSyncedPatient: Patient | null = null;
	private skipNextSync = false;
	private matchBannerShown = false;

	readonly testIds = BI_DIMENSIONS;
	readonly biFeature = BI_FEATURE;
	readonly biContainer = BI_CONTAINER;

	state!: PatientFormsMachineState;
	matchLoading = false;
	initializing = true;

	// Snapshot of this card's translated strings, keyed by translation key. Card labels
	// read from here via tr() instead of the `translate` pipe: the pipe fails to refresh
	// text-node / projected content inside OnPush TLK components after the host
	// wipes+reloads translations on open (see UnifiedPatientsModule for the store-side
	// race). This snapshot is rebuilt on every onTranslationChange, and reading it in the
	// template lets the card's own change detection re-render the labels deterministically.
	private translated: Record<string, string> = {};

	formFields = { firstName: '', lastName: '', gender: '', chartNumber: '' };
	parsedDateOfBirth = '';

	constructor(
		public readonly machine: PatientFormsMachineService,
		private readonly machineServices: PatientFormsServicesToken,
		private readonly modalWindow: TlkModalWindowService,
		private readonly cdr: ChangeDetectorRef,
		private readonly translate: TranslateService,
		private readonly bi: UnifiedPatientsBiService
	) {}

	get iTeroPatient(): Patient | null {
		return this.state?.context.iTeroPatient ?? null;
	}

	get invisalignPatient(): Patient | null {
		return this.state?.context.invisalignPatient ?? null;
	}

	get isInvisalignToggleOn(): boolean {
		return this.state?.matches('patientCard.invisalignPatient.creating') ?? false;
	}

	get showInvisalignToggle(): boolean {
		return !this.isReadOnly && !this.headerMessage && !this.state?.matches('patientCard.invisalignPatient.selected');
	}

	get showLinkMessage(): boolean {
		return !!this.headerMessage;
	}

	get headerMessage(): string | null {
		if (this.state?.matches('patientCard.iTeroPatient.creating') && this.state?.matches('patientCard.invisalignPatient.selected')) {
			return 'PatientSection.LinkCreatingMessage';
		}
		if (this.state?.matches('patientCard.iTeroPatient.selected') && this.state?.matches('patientCard.invisalignPatient.selected')) {
			if (this.state.context.iTeroPatient?.system === 'both') {
				return 'PatientSection.LinkedPatientMessage';
			}

			return 'PatientSection.LinkingMessage';
		}

		return null;
	}

	get isEditable(): boolean {
		if (this.initializing) {
			return false;
		}

		return !!this.state && !this.isReadOnly && checkCardEditable(this.state);
	}

	get isOffline(): boolean {
		return this.state?.matches('patientCard.connectivity.offline') ?? false;
	}

	get isReadOnly(): boolean {
		return this.state?.matches('patientCard.readOnly.yes') ?? false;
	}

	get hasPatientData(): boolean {
		return !!(this.iTeroPatient || this.invisalignPatient);
	}

	get showSearchButton(): boolean {
		return (
			!this.isReadOnly &&
			!this.isOffline &&
			this.state?.matches('patientCard.iTeroPatient.creating') &&
			!this.state?.matches('patientCard.invisalignPatient.selected')
		);
	}

	get showEditButton(): boolean {
		return (
			!this.isReadOnly &&
			(this.state?.matches('patientCard.iTeroPatient.selected') ||
				(this.state?.matches('patientCard.iTeroPatient.creating') && this.state?.matches('patientCard.invisalignPatient.selected')))
		);
	}

	get cardMatchPatient(): Patient | null {
		const matches = this.state?.context.cardMatchingPatients;

		if (!matches || matches.length === 0) {
			return null;
		}

		return matches[0];
	}

	get iTeroState(): string {
		if (this.state?.matches('patientCard.iTeroPatient.creating')) {
			return 'creating';
		}

		return 'selected';
	}

	get invisalignState(): string {
		if (this.state?.matches('patientCard.invisalignPatient.none')) {
			return 'none';
		}

		if (this.state?.matches('patientCard.invisalignPatient.creating')) {
			return 'creating';
		}

		return 'selected';
	}

	ngOnInit(): void {
		const lang = this.translate.currentLang || this.translate.defaultLang || 'en';

		this.translate.setTranslation(lang, translations, true);
		this.refreshTranslations();

		const previewSub = this.machine.preview$.subscribe(patient => {
			if (patient && this.initializing) {
				this.syncFormFromContext(patient);
				this.cdr.markForCheck();
			}
		});

		this.subscription = this.machine.state$.subscribe(state => {
			const prev = this.state;

			this.state = state;
			if (this.initializing && state.context.iTeroPatient) {
				this.initializing = false;
				this.reportOpened();
			}
			if (!this.initializing) {
				this.syncFormFromContext(state.context.iTeroPatient);
			}
			this.matchLoading = state.matches('patientMatch.loading');
			this.trackMatchBanner(!!this.cardMatchPatient);
			this.handleModalTransitions(prev, state);
			this.cdr.markForCheck();
		});

		const readySub = this.machine.ready$.subscribe((ready: boolean) => {
			if (ready && this.initializing) {
				this.initializing = false;
				this.reportOpened();
				if (!this.lastSyncedPatient) {
					this.syncFormFromContext(this.state?.context?.iTeroPatient ?? null);
				}
				this.cdr.markForCheck();
			}
		});

		// Keep the card's translated-string snapshot in sync when translations change
		// (e.g. after the host's reloadLang() reloads them on open). Reading the snapshot
		// in the template + markForCheck re-renders reliably where the `translate` pipe
		// does not for text-node / projected content in OnPush TLK components.
		const translationSub = this.translate.onTranslationChange.subscribe(() => {
			this.refreshTranslations();
			this.cdr.markForCheck();
		});

		this.subscription.add(previewSub);
		this.subscription.add(readySub);
		this.subscription.add(translationSub);

		this.formUpdate$.pipe(debounceTime(500)).subscribe(() => {
			this.skipNextSync = true;
			this.machine.send({
				type: 'CARD.FORM_UPDATED',
				patient: { id: null, ...this.formFields, dateOfBirth: this.parsedDateOfBirth, system: 'iTero' as const }
			});
		});

		this.subscription.add(this.machine.rxSaved$.subscribe(() => this.reportRxSaved()));
	}

	ngOnDestroy(): void {
		this.formUpdate$.complete();
		this.subscription.unsubscribe();
		this.matchLoading = false;
	}

	onSearch(): void {
		this.machine.send({ type: 'CARD.SEARCH_CLICKED' });
	}

	onClear(): void {
		this.machine.send({ type: 'CARD.CLEAR_CLICKED' });
	}

	onEditorClicked(): void {
		this.machine.send({ type: 'CARD.EDITOR_CLICKED' });
	}

	onInvisalignToggle(): void {
		this.machine.send({ type: 'CARD.INVISALIGN_TOGGLED' });
	}

	onFieldUpdate(): void {
		this.formUpdate$.next();
	}

	onDobChanged(isoDate: string): void {
		this.parsedDateOfBirth = isoDate;
		this.formUpdate$.next();
	}

	onReviewMatches(): void {
		this.machine.send({ type: 'CARD.REVIEW_MATCHES_CLICKED' });
	}

	/** Card label lookup backed by the translated snapshot (see refreshTranslations). */
	tr(key: string | null): string {
		if (!key) {
			return '';
		}

		return this.translated[key] ?? key;
	}

	/**
	 * Rebuilds the translated-string snapshot for every card label. Called on init and on
	 * every onTranslationChange so tr() always reflects the current store. Works around
	 * @ngx-translate's `translate` pipe not refreshing text-node / projected content inside
	 * OnPush TLK components after the host wipes+reloads translations on open.
	 */
	private refreshTranslations(): void {
		const keys = [
			'PatientSection.Patient',
			'PatientSection.CreateInvisalignPatient',
			'PatientSection.SearchPatient',
			'PatientSection.Edit',
			'PatientSection.Clear',
			'PatientSection.Gender',
			'PatientSection.MatchExists',
			'PatientSection.ReviewAndImport',
			'PatientSection.LinkCreatingMessage',
			'PatientSection.LinkedPatientMessage',
			'PatientSection.LinkingMessage'
		];

		this.translated = keys.reduce<Record<string, string>>((acc, key) => {
			acc[key] = this.translate.instant(key);

			return acc;
		}, {});
	}

	private reportOpened(): void {
		const isToggleHidden = this.state?.matches('patientCard.invisalignPatient.selected');
		const toggleValue: BiDimensionValues[typeof BI_DIMENSIONS.toggle] = isToggleHidden
			? 'hidden'
			: this.state?.context.invisalignToggle
				? 'on'
				: 'off';

		void this.bi.reporter?.trackStage({
			object: BI_FEATURE,
			stage: 'opened',
			previous: '',
			description: '',
			trigger: 'system',
			customAttributes: { [BI_DIMENSIONS.toggle]: toggleValue }
		});
	}

	private reportRxSaved(): void {
		if (!this.state?.context?.iTeroPatient) {
			return;
		}

		void this.bi.reporter?.trackStage({
			object: BI_FEATURE,
			stage: 'rx-saved',
			previous: '',
			description: '',
			trigger: 'system',
			customAttributes: buildSaveDimensions(this.state.context, this.state.matches.bind(this.state))
		});
	}

	private trackMatchBanner(visible: boolean): void {
		if (visible === this.matchBannerShown) {
			return;
		}

		this.matchBannerShown = visible;
		void this.bi.reporter?.trackStage({
			object: BI_DIMENSIONS.matchBanner,
			stage: visible ? 'shown' : 'hidden',
			previous: visible ? '' : 'shown',
			description: '',
			trigger: 'system',
			customAttributes: { feature: BI_FEATURE }
		});
	}

	// Side-effect handler: opens modals in response to machine state transitions.
	// Modals are opened/closed here (not in XState actions) because they require Angular DI (TlkModalWindowService).
	// The machine controls WHEN modals open via state transitions; this method translates transitions into modal calls.
	private handleModalTransitions(prev: PatientFormsMachineState | undefined, next: PatientFormsMachineState): void {
		if (prev?.matches('patientSearch.closed') && !next.matches('patientSearch.closed')) {
			this.openSearchModal();
		}

		if (!prev?.matches('patientLinking.active') && next.matches('patientLinking.active')) {
			this.openLinkingModal();
		}

		// Only open for UI-visible states. Silent states (checkingDuplicateFromLink/FromFill/FromEdit)
		// invoke a service with no UI — opening the editor there would show a form prematurely.
		if (prev?.matches('patientEditor.closed') && !next.matches('patientEditor.closed')) {
			if (
				next.matches('patientEditor.editingItero') ||
				next.matches('patientEditor.editingIteroFromLink') ||
				next.matches('patientEditor.fillingInvisalignFields')
			) {
				const mode: PatientEditorMode = next.matches('patientEditor.fillingInvisalignFields')
					? 'fillingInvisalignFields'
					: 'editingItero';

				this.openEditorModal(mode);
			}
		}

		// Duplicate found during link flow: the editor wasn't open yet (silent check ran first).
		// Guard: on retry (user saves, check runs again, fails again), the editor is already open — don't open a second one.
		if (prev?.matches('patientEditor.checkingDuplicateFromLink') && next.matches('patientEditor.editingIteroFromLink')) {
			if (!this.editorRef) {
				this.openEditorModal('editingItero');
			}
		}

		// Machine returned editor to closed (save succeeded, cancelled, etc.) — dismiss the modal.
		if (!prev?.matches('patientEditor.closed') && next.matches('patientEditor.closed')) {
			this.editorRef?.close();
			this.editorRef = null;
		}
	}

	private openSearchModal(): void {
		const isMatchesMode = this.state.matches('patientSearch.searchingMatches');
		const ref = this.modalWindow.open<Patient | undefined>(PatientSearchContentComponent, {
			width: '800px',
			height: '640px',
			data: {
				recentPatients$: this.machineServices.getRecentPatients(),
				matchingPatients: this.state.context.cardMatchingPatients,
				mode: isMatchesMode ? 'matches' : 'recent'
			}
		});

		ref.closed.subscribe(result => {
			if (result) {
				this.machine.send({ type: 'SEARCH.PATIENT_SELECTED', patient: result });
			} else {
				this.machine.send({ type: 'SEARCH.CANCELLED' });
			}
		});
	}

	private openLinkingModal(): void {
		const source = this.state.context.pendingIteroPatient || this.state.context.pendingInvisalignPatient;

		if (!source) {
			return;
		}

		const direction: 'iTero' | 'Invisalign' = source.system === 'iTero' ? 'iTero' : 'Invisalign';
		const committedItero = this.state.context.iTeroPatient;
		const confirmOnly =
			direction === 'Invisalign' &&
			!!committedItero?.id &&
			this.state.context.matchingPatients.some(c => c.patient.id === committedItero.id);

		const candidates = confirmOnly
			? this.state.context.matchingPatients.filter(c => c.patient.id === committedItero?.id)
			: this.state.context.matchingPatients;

		const data: PatientLinkingData = {
			sources: [source],
			candidates,
			direction,
			canCreateNew: confirmOnly ? false : this.state.context.canCreateNew,
			confirmOnly
		};

		const ref = this.modalWindow.open<PatientLinkingResult>(PatientLinkingContentComponent, {
			width: '860px',
			height: '640px',
			data
		});

		ref.closed.subscribe(result => {
			if (!result) {
				this.machine.send({ type: 'LINKING.CANCELLED' });
			} else if (result.action === 'linked') {
				const isIteroSource = direction === 'iTero';

				this.machine.send({
					type: 'LINKING.LINKED',
					source: direction,
					iTeroPatient: isIteroSource ? source : result.target,
					invisalignPatient: isIteroSource ? result.target : source
				});
			} else if (result.action === 'createNew') {
				this.machine.send({ type: 'LINKING.CREATE_NEW' });
			} else if (result.action === 'skipInvisalign') {
				this.machine.send({ type: 'LINKING.SKIP_INVISALIGN' });
			} else if (result.action === 'dismissed') {
				this.machine.send({ type: 'LINKING.SKIP_INVISALIGN' });
			}
		});
	}

	private openEditorModal(mode: PatientEditorMode): void {
		const patient = this.state.context.pendingIteroPatient || this.state.context.iTeroPatient;

		if (!patient) {
			return;
		}

		const ref = this.modalWindow.open<PatientEditorResult>(PatientEditorContentComponent, {
			width: '640px',
			height: 'fit-content',
			data: { mode, patient }
		});

		this.editorRef = ref;

		ref.closed.subscribe(() => {
			this.editorRef = null;
			if (!this.state.matches('patientEditor.closed')) {
				this.machine.send({ type: 'EDITOR.CANCELLED' });
			}
		});
	}

	private syncFormFromContext(patient: Patient | null): void {
		if (this.skipNextSync) {
			this.skipNextSync = false;
			this.lastSyncedPatient = patient;

			return;
		}
		if (patient === this.lastSyncedPatient) {
			return;
		}
		this.lastSyncedPatient = patient;
		this.parsedDateOfBirth = patient?.dateOfBirth || '';
		this.formFields = {
			firstName: patient?.firstName || '',
			lastName: patient?.lastName || '',
			gender: patient?.gender || '',
			chartNumber: patient?.chartNumber || ''
		};
	}
}
